import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Unlink, ChevronDown, ChevronRight, Plus, Loader2 } from "lucide-react";
import type { Finding, Audit, CorrectiveAction } from "@/app/pages/auditcentre/index";
import { FindingDetailDialog } from "./FindingDetailDialog";
import { FindingCreateDialog } from "./FindingCreateDialog";
import { FindingEditDialog } from "./FindingEditDialog";
import { CorrectiveActionDialog } from "./CorrectiveActionDialog";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// DED-1141: Updated interface to include corrective action handlers
interface AuditLinkedFindingsTableProps {
  findings: (Finding & { auditId?: string })[];
  audit: Audit;
  isLoading?: boolean;
  onLink: (findingId: string, auditId: string) => Promise<boolean>;
  onUnlink: (findingId: string) => Promise<boolean>;
  onUpdate: (findingId: string, updates: Partial<Finding>) => Promise<boolean>;
  onCreate: (finding: { summary: string; owner: string; status: "Open" | "Closed"; type: "Minor NC" | "Major NC" | "OFI"; auditId?: string }) => Promise<boolean>;
  auditService: AuditService;
  // DED-1141: Corrective action handlers for inline management
  correctiveActions?: CorrectiveAction[];
  onCreateCorrectiveAction?: (action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CorrectiveAction>;
  onUpdateCorrectiveAction?: (actionId: string, updates: Partial<CorrectiveAction>) => Promise<boolean>;
  onUnlinkCorrectiveAction?: (actionId: string) => Promise<boolean>;
  // DED-973: Controls to inherit from parent audit
  inheritedControls?: string[];
}

export function AuditLinkedFindingsTable({
  findings,
  audit,
  isLoading = false,
  onLink,
  onUnlink,
  onUpdate,
  onCreate,
  auditService,
  correctiveActions = [],
  onCreateCorrectiveAction,
  onUpdateCorrectiveAction,
  onUnlinkCorrectiveAction,
  inheritedControls = [],
}: AuditLinkedFindingsTableProps) {
  const { userIdToName } = useTenancy();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<Finding & { auditId?: string } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // DED-1141: Track expanded findings for corrective actions dropdown
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());
  // DED-1141: Corrective action dialog state
  const [caDialogOpen, setCaDialogOpen] = useState(false);
  const [selectedCA, setSelectedCA] = useState<CorrectiveAction | null>(null);
  const [caFindingId, setCaFindingId] = useState<string | null>(null);
  // DED-1141: Local corrective actions state for real-time updates
  const [localCorrectiveActions, setLocalCorrectiveActions] = useState<CorrectiveAction[]>(correctiveActions);

  // DED-1141: Sync local state with props
  useEffect(() => {
    setLocalCorrectiveActions(correctiveActions);
  }, [correctiveActions]);

  // DED-1141: Toggle finding expansion
  const toggleFindingExpansion = (findingId: string) => {
    setExpandedFindings(prev => {
      const next = new Set(prev);
      if (next.has(findingId)) {
        next.delete(findingId);
      } else {
        next.add(findingId);
      }
      return next;
    });
  };

  // DED-1141: Get corrective actions for a specific finding
  const getCorrectiveActionsForFinding = (findingId: string): CorrectiveAction[] => {
    return localCorrectiveActions.filter(ca => ca.findingId === findingId);
  };

  // DED-1141: Handle creating a corrective action for a finding
  const handleCreateCA = async (data: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!onCreateCorrectiveAction || !caFindingId) return;
    
    try {
      const newAction = await onCreateCorrectiveAction({
        ...data,
        findingId: caFindingId,
        auditId: audit.id,
      });
      // Update local state immediately
      setLocalCorrectiveActions(prev => [...prev, newAction]);
      setCaDialogOpen(false);
      setSelectedCA(null);
      setCaFindingId(null);
    } catch (error) {
      console.error("Failed to create corrective action:", error);
    }
  };

  // DED-1141: Handle updating a corrective action
  const handleUpdateCA = async (data: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!onUpdateCorrectiveAction || !selectedCA?.id) return;
    
    try {
      await onUpdateCorrectiveAction(selectedCA.id, data);
      // Update local state immediately
      setLocalCorrectiveActions(prev => 
        prev.map(ca => ca.id === selectedCA.id ? { ...ca, ...data } : ca)
      );
      setCaDialogOpen(false);
      setSelectedCA(null);
      setCaFindingId(null);
    } catch (error) {
      console.error("Failed to update corrective action:", error);
    }
  };

  return (
    <div className="glass-card p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Linked Findings</h2>
        <Button variant="default" onClick={() => setCreateDialogOpen(true)}>
          + New Finding
        </Button>
      </div>

      {/* DED-1141: Custom table with expandable corrective actions */}
      <div className="relative rounded-md border">
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading findings...</span>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {findings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No findings linked to this audit
                </TableCell>
              </TableRow>
            ) : (
              findings.map((finding) => {
                const isExpanded = expandedFindings.has(finding.id);
                const findingCAs = getCorrectiveActionsForFinding(finding.id);
                const hasCorrectiveActions = findingCAs.length > 0;

                return (
                  <React.Fragment key={finding.id}>
                    {/* Finding row */}
                    <TableRow className="hover:bg-muted/50">
                      <TableCell className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => toggleFindingExpansion(finding.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="medium">{finding.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={finding.status === "Open" ? "destructiveSubtle" : "successSubtle"}>
                          {finding.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{finding.summary}</span>
                          {hasCorrectiveActions && (
                            <Badge variant="outline" className="shrink-0">
                              {findingCAs.length} CA{findingCAs.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {userIdToName[finding.owner] || finding.owner}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedFinding(finding);
                              setDialogOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              setSelectedFinding(finding);
                              setEditDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => onUnlink(finding.id)}
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* DED-1141: Expandable corrective actions section */}
                    {isExpanded && (
                      <TableRow className="bg-muted/30 hover:bg-muted/40">
                        <TableCell colSpan={6} className="p-0">
                          <div className="px-8 py-4 border-l-4 border-primary/30">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-medium text-muted-foreground">
                                Corrective Actions for this Finding
                              </h4>
                              {onCreateCorrectiveAction && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setCaFindingId(finding.id);
                                    setSelectedCA(null);
                                    setCaDialogOpen(true);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Corrective Action
                                </Button>
                              )}
                            </div>

                            {findingCAs.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic py-2">
                                No corrective actions linked to this finding
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {findingCAs.map((ca) => (
                                  <div
                                    key={ca.id}
                                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <Badge variant={ca.status === "Open" ? "destructiveSubtle" : "successSubtle"}>
                                        {ca.status}
                                      </Badge>
                                      <span className="text-sm truncate">{ca.summary}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="text-xs text-muted-foreground">
                                        {userIdToName[ca.owner] || ca.owner}
                                      </span>
                                      {onUpdateCorrectiveAction && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0"
                                          onClick={() => {
                                            setCaFindingId(finding.id);
                                            setSelectedCA(ca);
                                            setCaDialogOpen(true);
                                          }}
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                      )}
                                      {onUnlinkCorrectiveAction && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                          onClick={async () => {
                                            await onUnlinkCorrectiveAction(ca.id);
                                            setLocalCorrectiveActions(prev =>
                                              prev.map(a => a.id === ca.id ? { ...a, findingId: undefined } : a)
                                            );
                                          }}
                                        >
                                          <Unlink className="h-3 w-3" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Finding Detail Dialog */}
      <FindingDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        finding={selectedFinding}
        onActionCreated={(action) => {
          if (selectedFinding) {
            setSelectedFinding({
              ...selectedFinding,
              correctiveActions: [...(selectedFinding.correctiveActions || []), action]
            });
            setLocalCorrectiveActions(prev => [...prev, action]);
          }
        }}
        onActionUpdated={(actionId, updates) => {
          if (selectedFinding?.correctiveActions) {
            setSelectedFinding({
              ...selectedFinding,
              correctiveActions: selectedFinding.correctiveActions.map(ca =>
                ca.id === actionId ? { ...ca, ...updates } : ca
              )
            });
            setLocalCorrectiveActions(prev =>
              prev.map(ca => ca.id === actionId ? { ...ca, ...updates } : ca)
            );
          }
        }}
        onActionDeleted={(actionId) => {
          if (selectedFinding?.correctiveActions) {
            setSelectedFinding({
              ...selectedFinding,
              correctiveActions: selectedFinding.correctiveActions.filter(ca => ca.id !== actionId)
            });
            setLocalCorrectiveActions(prev => prev.filter(ca => ca.id !== actionId));
          }
        }}
        auditService={auditService}
      />

      {/* Finding Create Dialog */}
      <FindingCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={onCreate}
        auditOptions={[{ id: audit.id, label: `${audit.type} — ${audit.scope}` }]}
        defaultAuditId={audit.id}
        disableAuditSelection={true}
      />

      {/* Finding Edit Dialog */}
      <FindingEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        finding={selectedFinding}
        onSave={async (updates) => {
          if (!selectedFinding?.id || !onUpdate) return false;

          setIsSubmitting(true);
          try {
            return await onUpdate(selectedFinding.id, updates);
          } catch (error) {
            console.error("Failed to update finding:", error);
            return false;
          } finally {
            setIsSubmitting(false);
          }
        }}
        isSubmitting={isSubmitting}
      />

      {/* DED-1141: Corrective Action Dialog for creating/editing CAs under findings */}
      <CorrectiveActionDialog
        open={caDialogOpen}
        onOpenChange={(open) => {
          setCaDialogOpen(open);
          if (!open) {
            setSelectedCA(null);
            setCaFindingId(null);
          }
        }}
        action={selectedCA}
        findings={findings}
        inheritedControls={inheritedControls}
        onSave={async (data) => {
          if (selectedCA?.id) {
            await handleUpdateCA(data);
          } else {
            await handleCreateCA(data);
          }
        }}
      />
    </div>
  );
}
